import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Link, Navigate, useLocation } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { TERMS_VERSION } from '../lib/constants'
import { useAuth } from '../contexts/AuthContext'

type AuthMode = 'sign-in' | 'register'

interface RegistrationForm {
  email: string
  password: string
  confirmPassword: string
  displayName: string
  surname: string
  affiliation: string
  orcid: string
  scholarUrl: string
  researchInterests: string
  acceptedTerms: boolean
}

const EMPTY_REGISTRATION: RegistrationForm = {
  email: '',
  password: '',
  confirmPassword: '',
  displayName: '',
  surname: '',
  affiliation: '',
  orcid: '',
  scholarUrl: '',
  researchInterests: '',
  acceptedTerms: false,
}

export function AuthPage() {
  const { user } = useAuth()
  const location = useLocation()
  const [mode, setMode] = useState<AuthMode>('sign-in')
  const [signInEmail, setSignInEmail] = useState('')
  const [signInPassword, setSignInPassword] = useState('')
  const [registration, setRegistration] = useState<RegistrationForm>(EMPTY_REGISTRATION)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [sending, setSending] = useState(false)

  const destination = useMemo(() => {
    return typeof location.state === 'object' &&
      location.state !== null &&
      'from' in location.state &&
      typeof location.state.from === 'string'
      ? location.state.from
      : '/dashboard'
  }, [location.state])

  useEffect(() => {
    setMessage(null)
    setError(null)
  }, [mode])

  if (user) return <Navigate to={destination} replace />

  const signIn = async (event: FormEvent) => {
    event.preventDefault()
    setSending(true)
    setError(null)
    setMessage(null)

    const { error: authError } = await supabase.auth.signInWithPassword({
      email: signInEmail.trim(),
      password: signInPassword,
    })

    if (authError) setError(authError.message)
    setSending(false)
  }

  const register = async (event: FormEvent) => {
    event.preventDefault()
    setSending(true)
    setError(null)
    setMessage(null)

    if (registration.password.length < 8) {
      setError('Password must contain at least 8 characters.')
      setSending(false)
      return
    }

    if (registration.password !== registration.confirmPassword) {
      setError('The two passwords do not match.')
      setSending(false)
      return
    }

    if (!registration.acceptedTerms) {
      setError('You must accept the Terms of Service and acknowledge the Privacy Policy.')
      setSending(false)
      return
    }

    const clean = (value: string) => value.trim() || null
    const { data, error: authError } = await supabase.auth.signUp({
      email: registration.email.trim(),
      password: registration.password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth`,
        data: {
          display_name: clean(registration.displayName),
          surname: clean(registration.surname),
          affiliation: clean(registration.affiliation),
          orcid: clean(registration.orcid),
          scholar_url: clean(registration.scholarUrl),
          research_interests: clean(registration.researchInterests),
          registration_terms_version: TERMS_VERSION,
          privacy_acknowledged_at: new Date().toISOString(),
        },
      },
    })

    if (authError) {
      setError(authError.message)
    } else if (data.session) {
      setMessage('Account created. You are now signed in.')
    } else {
      setMessage(
        'Registration submitted. You will receive a confirmation email from “Rapid First Look”, possibly from an address ending in @brevosend.com. Please check both your inbox and spam folder. After confirming your email address, return here and sign in with your email and password.',
      )
      setRegistration(EMPTY_REGISTRATION)
    }

    setSending(false)
  }

  const updateRegistration = <K extends keyof RegistrationForm>(
    key: K,
    value: RegistrationForm[K],
  ) => {
    setRegistration((current) => ({ ...current, [key]: value }))
  }

  return (
    <section className="section auth-section">
      <div className="container narrow-container">
        <div className="auth-card">
          <p className="eyebrow">Author access</p>
          <div className="auth-tabs" role="tablist" aria-label="Account access">
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'sign-in'}
              className={`auth-tab ${mode === 'sign-in' ? 'auth-tab-active' : ''}`}
              onClick={() => setMode('sign-in')}
            >
              Sign in
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'register'}
              className={`auth-tab ${mode === 'register' ? 'auth-tab-active' : ''}`}
              onClick={() => setMode('register')}
            >
              Register
            </button>
          </div>

          {mode === 'sign-in' ? (
            <form onSubmit={signIn} className="stack-form">
              <label htmlFor="sign-in-email">Email address</label>
              <input
                id="sign-in-email"
                type="email"
                autoComplete="email"
                required
                value={signInEmail}
                onChange={(event) => setSignInEmail(event.target.value)}
                placeholder="you@example.org"
              />

              <label htmlFor="sign-in-password">Password</label>
              <input
                id="sign-in-password"
                type="password"
                autoComplete="current-password"
                required
                value={signInPassword}
                onChange={(event) => setSignInPassword(event.target.value)}
              />

              <button className="button button-primary" type="submit" disabled={sending}>
                {sending ? 'Signing in...' : 'Sign in'}
              </button>
              <small>
                Password recovery currently not available.
              </small>
            </form>
          ) : (
            <form onSubmit={register} className="stack-form">
              <div className="form-section-heading">
                <span>1</span>
                <div>
                  <h2>Account credentials</h2>
                  <p>Required</p>
                </div>
              </div>

              <label htmlFor="register-email">Email address</label>
              <input
                id="register-email"
                type="email"
                autoComplete="email"
                required
                value={registration.email}
                onChange={(event) => updateRegistration('email', event.target.value)}
                placeholder="you@example.org"
              />

              <div className="form-grid-two">
                <div>
                  <label htmlFor="register-password">Password</label>
                  <input
                    id="register-password"
                    type="password"
                    autoComplete="new-password"
                    minLength={8}
                    required
                    value={registration.password}
                    onChange={(event) => updateRegistration('password', event.target.value)}
                  />
                  <small>At least 8 characters.</small>
                </div>
                <div>
                  <label htmlFor="confirm-password">Confirm password</label>
                  <input
                    id="confirm-password"
                    type="password"
                    autoComplete="new-password"
                    minLength={8}
                    required
                    value={registration.confirmPassword}
                    onChange={(event) => updateRegistration('confirmPassword', event.target.value)}
                  />
                </div>
              </div>

              <div className="form-section-heading form-section-spacing">
                <span>2</span>
                <div>
                  <h2>Academic profile</h2>
                  <p>Optional</p>
                </div>
              </div>

              <div className="form-grid-two">
                <div>
                  <label htmlFor="register-name">First name</label>
                  <input
                    id="register-name"
                    autoComplete="given-name"
                    value={registration.displayName}
                    onChange={(event) => updateRegistration('displayName', event.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="register-surname">Last name</label>
                  <input
                    id="register-surname"
                    autoComplete="family-name"
                    value={registration.surname}
                    onChange={(event) => updateRegistration('surname', event.target.value)}
                  />
                </div>
              </div>

              <label htmlFor="register-affiliation">Affiliation</label>
              <input
                id="register-affiliation"
                value={registration.affiliation}
                onChange={(event) => updateRegistration('affiliation', event.target.value)}
                placeholder="University, research institute, company, or independent researcher"
              />

              <div className="form-grid-two">
                <div>
                  <label htmlFor="register-orcid">ORCID iD</label>
                  <input
                    id="register-orcid"
                    value={registration.orcid}
                    onChange={(event) => updateRegistration('orcid', event.target.value)}
                    placeholder="0000-0000-0000-0000"
                  />
                </div>
                <div>
                  <label htmlFor="register-scholar">Google Scholar profile</label>
                  <input
                    id="register-scholar"
                    type="url"
                    value={registration.scholarUrl}
                    onChange={(event) => updateRegistration('scholarUrl', event.target.value)}
                    placeholder="https://scholar.google.com/..."
                  />
                </div>
              </div>

              <label htmlFor="register-interests">Research interests</label>
              <textarea
                id="register-interests"
                rows={3}
                value={registration.researchInterests}
                onChange={(event) => updateRegistration('researchInterests', event.target.value)}
                placeholder="For example: applied mathematics, scientific computing, biomedical imaging"
              />

              <div className="form-section-heading form-section-spacing">
                <span>3</span>
                <div>
                  <h2>Terms and privacy</h2>
                  <p>Required</p>
                </div>
              </div>

              <label className="check-row" htmlFor="register-terms">
                <input
                  id="register-terms"
                  type="checkbox"
                  checked={registration.acceptedTerms}
                  onChange={(event) => updateRegistration('acceptedTerms', event.target.checked)}
                  required
                />
                <span>
                  I accept the <Link to="/terms">Terms of Service</Link> and confirm that I
                  have read the <Link to="/privacy">Privacy Policy</Link>.
                </span>
              </label>

              <button className="button button-primary" type="submit" disabled={sending}>
                {sending ? 'Creating account...' : 'Create account'}
              </button>
            </form>
          )}

          {message && <div className="notice notice-success">{message}</div>}
          {error && <div className="notice notice-error">{error}</div>}
        </div>
      </div>
    </section>
  )
}
